<!-- app-subdomain-creators/src/Components/NewsfeedSearchModal.vue -->

<template>

    <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
  <div
  v-if="open"
  class="fixed inset-0 bg-zinc-200 bg-opacity-[0.80] flex justify-center items-start pt-24 sm:pt-56 z-50 "
  @keydown.esc.prevent.stop="close"
>

    <!-- click backdrop to close -->
    <div
      class="absolute inset-0"
      @click="close"
    ></div>

    <!-- Modal panel -->
    <div
      class="relative w-full max-w-2xl rounded-2xl bg-white shadow-[0_0_40px_rgba(15,23,42,0.28)] border border-zinc-200/80 px-4 py-4 sm:px-6 sm:py-5 "
      @click.stop
    >
      <!-- Close button -->
      <button
        type="button"
        class="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2D8CFF]"
        @click="close"
      >
        <span class="sr-only">Close</span>
        <svg
          class="h-4 w-4"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5.25 5.25L14.75 14.75M14.75 5.25L5.25 14.75"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <!-- Search bar -->
      <form @submit.prevent="handleSubmit">
        <div
  class="flex items-center gap-3 rounded-full bg-zinc-100 px-4 py-2 border border-zinc-200
        outline-none focus:outline-none focus:ring-0 mt-10"
>

          <!-- search icon -->

          <MagnifyingGlassIcon class="w-7 text-zinc-300"></MagnifyingGlassIcon>
          
          <input
            ref="inputEl"
            v-model="query"
            type="text"
            class="search-input w-full bg-transparent text-[15px] text-zinc-900
                    placeholder-zinc-400
                    border-none outline-none focus:outline-none focus:ring-0"
            :placeholder="`Search ${groupName || 'this group'}`"
            @keyup.enter.prevent="handleSubmit"
            />

        </div>
      </form>

            <!-- Search results -->
      <div class="mt-3 space-y-2 text-[13px]">
        <!-- Loading -->
        <div
          v-if="loading"
          class="text-[12px] text-zinc-400"
        >
          Searching…
        </div>

        <!-- Error -->
        <div
          v-if="!loading && searchError"
          class="text-[12px] text-red-500"
        >
          {{ searchError }}
        </div>

        <!-- Results list -->
        <div
          v-if="!loading && !searchError && results.length"
          class="space-y-1"
        >
          <div class="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Results for “{{ lastQuery }}”
          </div>

          <ul class="max-h-64 overflow-y-auto chat-scroll">
            <li
              v-for="item in results"
              :key="`${item.type}-${item.commentId || item.postId}`"
              class="px-2 py-2 rounded-lg hover:bg-zinc-50 cursor-pointer"
              @click="handleResultClick(item)"
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex px-2 py-[2px] rounded-full text-[11px] font-medium"
                  :class="item.type === 'post'
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-emerald-50 text-emerald-700'"
                >
                  {{ item.type === 'post' ? 'Post' : 'Comment' }}
                </span>
              </div>

              <div class="mt-1 text-[13px] text-zinc-800 leading-snug line-clamp-2">
                {{ item.snippet }}
              </div>
            </li>
          </ul>

          <button
            v-if="hasMore"
            type="button"
            class="mt-1 text-[12px] text-[#2D8CFF] hover:underline"
            @click="loadMoreResults"
          >
            Load more results
          </button>
        </div>

        <!-- No results state -->
        <div
          v-else-if="!loading && !searchError && lastQuery && !results.length"
          class="text-[12px] text-zinc-500"
        >
          No results for “{{ lastQuery }}”.
        </div>
      </div>


      <!-- Body -->
      <div class="mt-4 space-y-4 text-[13px]">
        <!-- Explore this group -->
        <div>
          <div class="mb-1 text-base font-semibold uppercase tracking-wide text-zinc-800">
            Explore this group
          </div>

          <!-- Your activity row -->
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-xl px-2 py-2 hover:bg-zinc-100 text-left"
            @click="handleOpenYourPosts"
          >
            <img
              v-if="currentUserAvatar"
              :src="currentUserAvatar"
              alt=""
              class="h-8 w-8 rounded-full object-cover"
            />
            <div
              v-else
              class="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-[11px] font-semibold text-zinc-600"
            >
              {{ initials }}
            </div>

            <div class="flex flex-col">
              <div class="text-[13px] font-semibold text-zinc-900 leading-snug">
                {{ currentUserName }}
              </div>
              <div class="text-[12px] text-zinc-500 leading-snug">
                See your group activity.
              </div>
            </div>
          </button>
        </div>

        <!-- Divider -->
        <div class="h-px bg-zinc-200/80 text-center"></div>

        <!-- Bottom helper text -->
        <div class="text-sm text-zinc-500 text-center">
          <span class="font-medium">Looking for something?</span><br>
          <span>
            &nbsp;Search
            <span class="font-semibold text-zinc-900">
              {{ groupName || 'this group' }}
            </span>
            &nbsp;for posts, comments or members.
          </span>
        </div>
      </div>
    </div>
  </div>
  </Transition>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  groupName: {
    type: String,
    default: '',
  },
  currentUserName: {
    type: String,
    default: 'You',
  },
  currentUserAvatar: {
    type: String,
    default: null,
  },
  // ⬇️ we need this to scope the search to the current newsfeed instance
  featureInstanceId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['update:open', 'search', 'open-your-posts', 'open-search-tab',]);

const query = ref('');
const inputEl = ref(null);

/** ──────────────────────────────
 *  Avatar initials for fallback
 *  ─────────────────────────────*/
const initials = computed(() => {
  if (!props.currentUserName) return '';
  const parts = props.currentUserName.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts[1]?.[0] || '';
  return (first + last).toUpperCase();
});

/** ──────────────────────────────
 *  Search state
 *  ─────────────────────────────*/
const loading = ref(false);
const searchError = ref('');
const results = ref([]);          // [{ type, postId, commentId, snippet, createdAt }]
const limit = ref(10);
const offset = ref(0);
const hasMore = ref(false);
const lastQuery = ref('');

// debounce timer
let searchDebounceTimer = null;

/** ──────────────────────────────
 *  Modal helpers
 *  ─────────────────────────────*/
function close() {
  emit('update:open', false);
}

async function handleSubmit() {
  const trimmed = query.value.trim();
  if (!trimmed || !props.featureInstanceId) return;

  // still emit for parent if they ever want to track terms
  emit('search', trimmed);

  offset.value = 0;
  await runSearch({ append: false });
}

function handleOpenYourPosts() {
  emit('open-your-posts');
  emit('update:open', false);
}

/** ──────────────────────────────
 *  Core search call (to BFF)
 *  ─────────────────────────────*/
async function runSearch({ append = false } = {}) {
  const term = query.value.trim();
  if (!term || !props.featureInstanceId) return;
  if (loading.value) return;

  loading.value = true;
  searchError.value = '';
  lastQuery.value = term;

  if (!append) {
    results.value = [];
    hasMore.value = false;
    offset.value = 0;
  }

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      q: term,
      limit: String(limit.value),
      offset: String(offset.value),
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/search?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || !data || data.success === false) {
      console.error('[NewsfeedSearchModal] search error:', res.status, data);
      searchError.value =
        (data && data.error) ||
        'There was a problem running your search.';
      return;
    }

    const incoming = Array.isArray(data.results) ? data.results : [];

    if (append) {
      results.value = results.value.concat(incoming);
    } else {
      results.value = incoming;
    }

    const pageCount = incoming.length;
    const backendOffset =
      typeof data.offset === 'number' ? data.offset : offset.value;

    offset.value = backendOffset + pageCount;
    hasMore.value = !!data.hasMore && pageCount > 0;
  } catch (err) {
    console.error('[NewsfeedSearchModal] network error:', err);
    searchError.value =
      'Network error while searching. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function loadMoreResults() {
  if (!hasMore.value) return;
  await runSearch({ append: true });
}

function handleResultClick(item) {
  const term = (lastQuery.value || query.value || '').trim();
  if (!term) {
    // fallback: just close if something weird happened
    emit('update:open', false);
    return;
  }

  emit('open-search-tab', {
    term,
    // we *could* send the clicked item for future “scroll to”
    // but for now we only care about the term:
    initialResult: item,
  });

  // close the modal
  emit('update:open', false);
}


/** ──────────────────────────────
 *  Live search on typing (debounced)
 *  ─────────────────────────────*/
// ⭐ when query changes, wait a bit then search
watch(query, (newVal) => {
  if (!props.open) return;                // only when modal visible
  const term = newVal.trim();

  // clear old timer
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }

  // if input cleared, reset results
  if (!term) {
    results.value = [];
    lastQuery.value = '';
    hasMore.value = false;
    searchError.value = '';
    offset.value = 0;
    return;
  }

  if (!props.featureInstanceId) return;

  // debounce ~400ms after last keystroke
  searchDebounceTimer = setTimeout(() => {
    offset.value = 0;
    runSearch({ append: false });
  }, 400);
});

/** ──────────────────────────────
 *  Focus when opening
 *  ─────────────────────────────*/
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      requestAnimationFrame(() => {
        if (inputEl.value) {
          inputEl.value.focus();
          inputEl.value.select();
        }
      });
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.open && inputEl.value) {
    inputEl.value.focus();
  }
});

// ⭐ clean up timer
onBeforeUnmount(() => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }
});
</script>



<style scoped>
.search-input:focus,
.search-input:focus-visible {
  outline: none;
  box-shadow: none;
}

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
